import React, { useEffect, useState } from 'react';
import { useBusiness, useTitle } from '../Hooks';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Location, Posting, SetLoading } from '../Classes';
import { Button, Header, Input, Label, Message, Placeholder, Toast, Pagination } from 'react-frontier';
import { Autocomplete, Card, GoogleMap, Modal } from '../components';
import { firstBy } from 'thenby';
import { Link } from 'react-router-dom';
import { bindClick, bindFormChange } from '@tempu/components/Util';
import { getDurationTime } from '../Util';
import Validator from '@tempu/components/Validator';
import moment from 'moment';
import API, { CDN_URL } from '../API';

const PAGE_SIZE = 20;

interface PostingCreateData{
	posting_name: string,
	location: Location
}

var BusinessPostings = ()=>{
	var params = useParams<{ id: string }>();
	var business_id = parseInt(params?.id);
	var navigate = useNavigate();

	var { setTitle, setAction } = useTitle();
	var { hasAccess } = useBusiness(business_id);
	var [searchParams, setSearchParams] = useSearchParams();
	var [loadError, setLoadError] = useState<string>(null);
	var [postingList, setPostingList] = useState<Posting[]>(null);
	var [createModal, setCreateModal] = useState<boolean>(false);
	var [modalPrompts, setModalPrompts] = useState<string[]>(null);
	var [postingForm, setPostingForm] = useState<PostingCreateData>({ posting_name: null, location: null });
	var [loading, setLoading] = useState<boolean>(false);
	var [page, setPage] = useState<number>(0);

	useEffect(()=>{
		if(searchParams.get('create')=='1'){
			setCreateModal(true);
			searchParams.delete('create');
			setSearchParams(searchParams);
		}
		setAction(
			<Button text='Nuevo empleo' iconName='plus-circle' color='black' onClick={bindClick(setCreateModal, true)} />
		);
	}, []);
	
	useEffect(()=>{
		setTitle('Empleos');
		if(!params || !business_id || Number.isNaN(business_id)) return;
		
		get();
	}, []);

	var get = (get_page?: number)=>{
		var offset = (get_page > 0) ? Math.max(0, (get_page || page) * PAGE_SIZE) : 0;
	
		setLoading(true);
		API.getBusinessPostings(business_id, false, offset, PAGE_SIZE).then(res=>{
			if(res.error) return setLoadError(res.message);
			setPostingList(res.data);
		}).catch(e=>{
			return setLoadError('Hubo un error inesperado cargando los empleos. (LCL-1)');
		}).finally(()=>{
			setLoading(false);
		})
	}

	var createPosting = async(setLoading: SetLoading)=>{
		var { valid, prompts } = Validator(postingForm, {
			posting_name: [{
				rule: 'minLength', params: [3], label: 'Nombre de empleo'
			}],
			location: [{
				rule: 'empty', label: 'Ubicación'
			}]
		});
		setModalPrompts(prompts);
		if(!valid) return;
		
		setLoading(true);
		API.createPosting(business_id, postingForm).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			Toast.success('Empleo creado!');
			
			return navigate(`/business/${business_id}/posting/${res.data.posting_id}`);
		}).catch(err=>{
			setModalPrompts(['Hubo un error al crear el empleo. (PCE-1)']);
		}).finally(()=>{
			setLoading(false);
		});
	}

	var onPageChange = (page: number)=>{
		setPage(page);
		get(page);
	}
	
	var onEditPostingForm = bindFormChange(postingForm, setPostingForm);
	
	return <div className="tp business-postings-screen">
		{loadError ? ( <Header text='Error' subtext={loadError} /> ) : loading ?
		(new Array(3).fill('TEMPU').map((a,i)=>(
			<Card placeholder placeholderImage className='posting' key={`CRDPLH-${i}`}>
				<div className="left">
					<Placeholder />
					<Placeholder />
					<Placeholder />
				</div>
			</Card>
		))) : postingList ? (
			postingList.sort(firstBy('posting_id', 'desc').thenBy('date_created', 'asc')).map(p=>{
				var posting_duration = getDurationTime(p.date_start, p.date_end);
				return (
					<Card className='posting' image={`${CDN_URL}/posting/${p.posting_id}/small.webp`} imageFallbackIcon='image' key={`BSNPST-${p.posting_id}`} to={`/business/${business_id}/posting/${p.posting_id}`} as={Link}>
						<div className="left">
							<div className="meta small">Empleo #{p.posting_id}</div>
							<div className="name">{p.posting_name}</div>
							{(p.date_start && p.date_end) ? (
								<div className="meta">
									{moment.unix(p.date_start).format('DD/MMM/YY HH:mm')}
									{posting_duration ? ` (${posting_duration})` : null}
								</div>
							) : (
								<div className="meta">Creado {moment.unix(p.date_created).format('DD/MMM/YY HH:mm')}</div>
							)}
							<div className="labels">
								<Label value={p.approved ? 'Aprobado' : 'En aprobación'} color={p.approved ? 'green' : null} iconName={p.approved ? 'check-circle' : 'times-circle'} />
								<Label value={p.published ? 'Publicado' : 'No publicado'} color={p.published ? 'green' : null} iconName={p.published ? 'check-circle' : 'times-circle'} />
							</div>
						</div>
					</Card>
				)
			})     
		) : null}
		{postingList && postingList.length === 0 && page === 0 ? (
			<Header text={'Sin empleos'} subtext={'El negocio no tiene empleos activos'} iconName='clipboard-question' />
		) : postingList && postingList.length === 0 && page !== 0 ? <Header text={'No se encontraron empleos'}  iconName='clipboard-question' /> : null}
		{postingList && postingList.length >= PAGE_SIZE || page !== 0 ? <div style={{ textAlign: 'center' }}>
			<Pagination page={page} onPageChange={onPageChange} />
		</div> :  null }
		<Modal open={createModal} onClose={bindClick(setCreateModal)} size='tiny'>
			<Modal.Header>Nuevo empleo</Modal.Header>
			<Modal.Content>
				<Input label='Nombre de empleo' value={postingForm.posting_name} onChange={onEditPostingForm('posting_name')}/>
				<Autocomplete
					label='Ubicación'
					comment='Ubicación de Google Maps del lugar de empleo.'
					placeholder="Buscar ubicación..."
					onPlaceSelected={onEditPostingForm('location')}
				/>
				{!!postingForm.location && (
					<GoogleMap location={postingForm.location} height={250} />
				)}
				<Message type="error" list={modalPrompts} />
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cerrar' basic onClick={bindClick(setCreateModal)} />
				<Button text='Crear empleo' color='black' onClick={createPosting}/>
			</Modal.Actions>
		</Modal>
	</div>
}

export default BusinessPostings;